/**
 * @author: Codex
 * @date: 2026/07/02
 * @desc: 普通下拉多选组件，基于 tea-component SelectMultiple 实现
 */
import React, { useRef } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { SelectMultiple as TeaSelectMultiple } from 'tea-component';
import Bubble from '@intelligence/v3/components/Bubble';
import { formatText, isNotEmpty } from '@intelligence/v3/utils/common';
import { defaultFilter, getOptionSearchText } from '@intelligence/v3/utils/select';

import type {
  SelectMultipleProps as TeaSelectMultipleProps,
  SelectOptionWithGroup as TeaSelectOptionWithGroup,
} from 'tea-component';

import './index.scss';

export interface ISelectMultipleOptionWithGroup extends TeaSelectOptionWithGroup {
  /** 选项图标 */
  icon?: React.ReactNode;
  /** 下拉按钮中展示的文本，不传时使用 text */
  buttonText?: React.ReactNode;
  /** 搜索匹配文本，不传时使用 text/value/buttonText/tooltip */
  searchText?: string;
}

export interface ISelectMultipleProps
  extends IBaseProps,
  Omit<TeaSelectMultipleProps, 'button' | 'options' | 'onChange'> {
  /** 是否显示按钮内的 label 名称，默认 true */
  isShowLabel?: boolean;
  /** 下拉按钮内的名称，支持国际化 key */
  label?: string | React.ReactNode;
  /** 下拉选项数据 */
  options: ISelectMultipleOptionWithGroup[];
  /** 已选数量大于等于该值时，按钮只展示数量，默认 2 */
  maxSelectionTextCount?: number;
  /** 全选状态下按钮展示文案，支持国际化 key */
  allText?: string | React.ReactNode;
  /** 空态文案，支持国际化 key */
  emptyText?: string | React.ReactNode;
  /** 选项气泡提示最大宽度，默认 500px */
  tooltipMaxWidth?: number | string;
  /** 是否展示内置全选项 */
  showAllOption?: boolean;
  /** 内置全选项文案，支持国际化 key */
  allOptionText?: string | React.ReactNode;
  /** 自定义按钮渲染 */
  button?: (selectedOptions: ISelectMultipleOptionWithGroup[]) => React.ReactNode;
  /** 选中值变化回调 */
  onChange?: (value: string[], context: any) => void;
}

const SelectMultiple: React.FC<ISelectMultipleProps> = (props) => {
  const {
    style,
    className,
    overlayClassName,
    boxClassName,
    matchButtonWidth = true,
    staging = false,
    size = 'm',
    appearance = 'button',
    searchable = false,
    autoClearSearchValue = true,
    isShowLabel = true,
    label = '',
    placeholder = 'dbi_please_select',
    searchPlaceholder = 'dbi_label_search',
    allText = 'dbi_all',
    emptyText = 'dbi_no_data',
    tooltipMaxWidth = 500,
    maxSelectionTextCount = 2,
    showAllOption = false,
    allOptionText = 'dbi_all_select',
    options = [],
    value = [],
    footer,
    tips,
    button,
    filter,
    onChange,
    onClose,
    allOption,
    ...restProps
  } = props;

  const { formatMessage } = useIntl();
  const currentValueRef = useRef<string[]>(value);
  const changeContextRef = useRef<any>();
  currentValueRef.current = value;

  const formatNode = (node: React.ReactNode): React.ReactNode => {
    if (typeof node === 'string') {
      return formatText(formatMessage, node);
    }

    return node;
  };

  const renderOptionContent = (option: ISelectMultipleOptionWithGroup) => {
    const icon = option?.icon;
    let iconElement: React.ReactNode = null;

    if (typeof icon === 'string') {
      iconElement = <img className="dbi-component-select-multiple-option__icon" src={icon} alt="" />;
    }

    if (icon && typeof icon !== 'string') {
      iconElement = <span className="dbi-component-select-multiple-option__icon">{icon}</span>;
    }

    return (
      <div className="dbi-component-select-multiple-option">
        {iconElement && iconElement}
        <span className="dbi-component-select-multiple-option__text">{formatNode(option?.text || option?.value)}</span>
      </div>
    );
  };

  const processedOptions = options.map((option) => {
    const optionText = renderOptionContent(option);
    let text: React.ReactNode = optionText;

    if (option?.tooltip) {
      text = (
        <Bubble maxWidth={tooltipMaxWidth} content={formatNode(option.tooltip)}>
          <div className="dbi-component-select-multiple-option-bubble">{optionText}</div>
        </Bubble>
      );
    }

    return {
      ...option,
      searchText: getOptionSearchText(option),
      text,
    };
  });

  const selectedOptions = options.filter((option) => value?.includes(option.value));
  const enabledOptions = options.filter((option) => !option.disabled);
  const enabledValues = enabledOptions.map((option) => option.value);
  const isSelectedAll = isNotEmpty(enabledValues) && enabledValues.every((optionValue) => value?.includes(optionValue));
  const getEnabledValues = (): string[] => {
    return enabledOptions
      .map((option) => {
        return option.value;
      })
      .filter((optionValue) => {
        return !!optionValue;
      });
  };

  const renderSelectedOptionNames = () => {
    const selectedNodes: React.ReactNode[] = [];

    selectedOptions.forEach((option, index) => {
      const optionText = option?.buttonText || option?.text || option?.value;
      selectedNodes.push(
        <React.Fragment key={option.value}>
          {formatNode(optionText)}
          {index < selectedOptions.length - 1 && <span className="dbi-component-select-multiple-button__gap">, </span>}
        </React.Fragment>,
      );
    });

    return selectedNodes;
  };

  const renderButton = () => {
    if (typeof button === 'function') {
      return button(selectedOptions);
    }

    let valueNode: React.ReactNode = (
      <span className="dbi-component-select-multiple-button__placeholder">{formatNode(placeholder)}</span>
    );

    if (isSelectedAll) {
      valueNode = formatNode(allText);
    }

    if (!isSelectedAll && isNotEmpty(value) && value.length >= maxSelectionTextCount) {
      valueNode = value.length;
    }

    if (!isSelectedAll && isNotEmpty(value) && value.length < maxSelectionTextCount) {
      valueNode = renderSelectedOptionNames();
    }

    return (
      <div className="dbi-component-select-multiple-button">
        {isShowLabel && label && (
          <span className="dbi-component-select-multiple-button__label">{formatNode(label)} </span>
        )}
        <span className="dbi-component-select-multiple-button__value">{valueNode}</span>
      </div>
    );
  };

  let tipsNode = tips;

  if (tipsNode === undefined && isNotEmpty(emptyText)) {
    tipsNode = (filteredOptions: ISelectMultipleOptionWithGroup[]) => {
      if (isNotEmpty(filteredOptions)) {
        return null;
      }

      return <div className="dbi-component-select-multiple-empty">{formatNode(emptyText)}</div>;
    };
  }

  let allOptionConfig = allOption;

  if (showAllOption && allOptionConfig === undefined) {
    allOptionConfig = {
      value: 'all',
      text: formatNode(allOptionText),
    };
  }

  return (
    <TeaSelectMultiple
      {...restProps}
      style={style}
      className={classnames('dbi-component-select-multiple', className, {
        'dbi-component-select-multiple--has-selected-value': isNotEmpty(value),
      })}
      overlayClassName={classnames('dbi-component-select-multiple-overlay', overlayClassName)}
      boxClassName={classnames('dbi-component-select-multiple-box', boxClassName)}
      matchButtonWidth={matchButtonWidth}
      staging={staging}
      size={size}
      appearance={appearance}
      searchable={searchable}
      autoClearSearchValue={autoClearSearchValue}
      filter={filter || defaultFilter}
      placeholder={formatNode(placeholder) as string}
      searchPlaceholder={formatNode(searchPlaceholder) as string}
      footer={footer}
      tips={tipsNode}
      button={renderButton}
      allOption={allOptionConfig}
      options={processedOptions}
      value={value}
      onChange={(payload, context) => {
        currentValueRef.current = payload;
        changeContextRef.current = context;
        onChange?.(payload, context);
      }}
      onClose={() => {
        if (currentValueRef.current.length === 0) {
          const nextValue = getEnabledValues();
          if (nextValue.length > 0) {
            currentValueRef.current = nextValue;
            onChange?.(nextValue, changeContextRef.current);
          }
        }
        onClose?.();
      }}
    />
  );
};

export default SelectMultiple;
